// Workspace context and permissions (ADR-0011).
//
// The app used to take the first membership row it found, which is wrong the
// moment someone works for two businesses: they would silently act in whichever
// workspace the database happened to return first. The active workspace is now
// an explicit choice, remembered per browser, and always sent to the server —
// which verifies it regardless, because a client-supplied workspace id is not
// authorization.
//
// Permissions here are for enabling controls, never for protecting anything.
// Every one of them is enforced again in RLS or in a permission-checked RPC, so
// a stale value can only ever hide something the user could have done, never
// reveal something they cannot.

import { supabase } from "./supabase";
import { asAppError } from "./entitlements";

export type WorkspaceKind = "influencer" | "business" | "agency";

export interface WorkspaceSummary {
  workspace_id: string;
  name: string;
  /** ADR-0013. Null means nobody has told us yet. Wording only, never authority. */
  kind: WorkspaceKind | null;
  is_owner: boolean;
  /** Whether *you* are inside this workspace's organisation or working for it. */
  relationship: "internal" | "external";
  /** What this workspace's owner called your company. Null unless external.
   *  A free-text label, not a reference — deliberately distinct from
   *  `organisation_id`, which is the real entity. */
  organisation: string | null;
  /** The organisation this workspace belongs to (ADR-0021), or null. Null is
   *  the ordinary case: a solo influencer never meets an organisation. */
  organisation_id: string | null;
  roles: string[];
  permissions: string[];
  plan: string;
}

const STORAGE_KEY = "simino.workspace";

export async function listMyWorkspaces(): Promise<WorkspaceSummary[]> {
  const { data, error } = await supabase.rpc("my_workspaces");
  if (error) throw asAppError(error);
  return (data ?? []) as WorkspaceSummary[];
}

/**
 * The remembered choice, unvalidated.
 *
 * It is sent to `my_session()` rather than resolved here (ADR-0027): whether
 * this id is still one of theirs is the server's question, and the answer comes
 * back as the workspace it actually used. This side only has to remember.
 */
export function rememberedWorkspaceId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode or blocked storage: the server picks, which it does anyway.
    return null;
  }
}

export function rememberWorkspace(workspaceId: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, workspaceId);
  } catch {
    // Not remembering is a small loss; failing here would not be.
  }
}

/** True when the active workspace grants this permission at workspace scope. */
export const allows = (ws: WorkspaceSummary | null, permission: string): boolean =>
  !!ws?.permissions?.includes(permission);

/** Presentation only. Unset workspaces read as a business, which is the neutral case. */
export const kindOf = (ws: WorkspaceSummary | null): WorkspaceKind => ws?.kind ?? "business";

export async function setWorkspaceKind(
  workspaceId: string, kind: WorkspaceKind,
): Promise<void> {
  const { error } = await supabase.rpc("set_workspace_kind", {
    p_workspace_id: workspaceId, p_kind: kind,
  });
  if (error) throw asAppError(error);
}

/**
 * What kind of business this workspace is (ADR-0013).
 *
 * It selects wording and form defaults and nothing else — no permission, quota
 * or feature reads it. The temptation with a type like this is to grow it into
 * a feature flag, at which point an SMB that picked the wrong word at signup is
 * locked out of half the product.
 *
 * The `pitch` is what an owner of that kind is told about themselves. An SMB
 * owner does not think of themselves as an influencer, and that is exactly the
 * point worth making to them: the people they need to reach are their own
 * customers, and the job is the same one.
 */
export const WORKSPACE_KIND: Record<WorkspaceKind, {
  label: string; blurb: string; pitch: string;
}> = {
  influencer: {
    label: "Creator or influencer",
    blurb: "You publish as yourself, and your audience follows you.",
    pitch: "Your accounts stay yours. Anyone you bring in — an editor, a manager, "
         + "an agency — publishes through the connection without ever holding the password.",
  },
  business: {
    label: "Business or brand",
    blurb: "You publish as a company to customers and prospects.",
    pitch: "You are an influencer too — you influence the people who buy from you. "
         + "A shop with 400 regulars moves more of them than a creator with 400,000 strangers, "
         + "so this works the same way for you as it does for a creator: post, approve, publish, "
         + "and let people help without handing over the keys.",
  },
  agency: {
    label: "Agency",
    blurb: "You publish on behalf of other people's brands.",
    pitch: "Each client is a separate workspace with its own accounts and billing. "
         + "Your people are invited into the client's workspace and scoped to the accounts "
         + "they work on — there is no master key that opens every client at once.",
  },
};

/**
 * What kind of account this person is running — read off the shape of their
 * memberships rather than off a flag.
 *
 * There is no user-level "is an agency" column and there should not be one: a
 * person is not an agency, an organisation is, and one identity legitimately
 * owns a business, freelances for a second and is on staff at a third
 * (ADR-0011). So the question is answered from two independent signals that can
 * disagree, and this returns both rather than collapsing them:
 *
 *   declared — the `kind` of the workspace they own. Self-described, free to
 *              be aspirational, and the only signal a new account has.
 *   observed — how many other people's workspaces they were let into, and
 *              whether they were let in as an outsider. Hard to game, because
 *              somebody else had to invite them and type their company name.
 *
 * What it cannot tell you, and the code must not pretend otherwise:
 *
 *   - An agency's *owner* may hold no client memberships at all — their staff
 *     hold them. No external memberships is not evidence against `agency`.
 *   - A new account has no observed signal whatsoever.
 *   - Email domain is not used. Inferring "gmail.com therefore influencer"
 *     writes a claim other screens act on off the back of a guess.
 */
export interface AccountShape {
  /** Workspaces they own, and the kind they chose for each. */
  owned: WorkspaceSummary[];
  /** Other people's workspaces they were invited into as an outsider. */
  guest: WorkspaceSummary[];
  /** Other people's workspaces they belong to as part of that company's team. */
  employed: WorkspaceSummary[];
  /** The kind they declared. Null when unasked; "mixed" when they own several
   *  workspaces of different kinds, which is a real answer and not a default. */
  declared: WorkspaceKind | "mixed" | null;
  /** The names their clients entered for them. Distinct, in first-seen order. */
  knownAs: string[];
  /** Behaviour, independent of what they said about themselves. */
  observed: "new" | "solo" | "employee" | "collaborator" | "multi-client";
  /** Set when the two signals point different ways and it is worth saying so. */
  note: string | null;
}

export function accountShape(workspaces: WorkspaceSummary[]): AccountShape {
  const owned = workspaces.filter((w) => w.is_owner);
  const rest = workspaces.filter((w) => !w.is_owner);
  const guest = rest.filter((w) => w.relationship === "external");
  const employed = rest.filter((w) => w.relationship === "internal");

  const kinds = [...new Set(owned.map((w) => w.kind).filter(Boolean))] as WorkspaceKind[];
  const declared: AccountShape["declared"] =
    kinds.length === 0 ? null : kinds.length === 1 ? kinds[0] : "mixed";

  const knownAs = [...new Set(guest.map((w) => w.organisation?.trim()).filter(Boolean))] as string[];

  const observed: AccountShape["observed"] =
    guest.length >= 3 ? "multi-client"
    : guest.length >= 1 ? "collaborator"
    : employed.length >= 1 ? "employee"
    : workspaces.length > 0 ? "solo"
    : "new";

  // Only two disagreements are worth raising. Everything else is a person using
  // the product in more than one way, which is allowed and normal.
  let note: string | null = null;
  if (guest.length >= 2 && (declared === "influencer" || declared === "business")) {
    note = `You work in ${guest.length} other businesses as an outside collaborator. `
         + "That is the agency and freelance shape, whatever your own workspace is set to.";
  } else if (declared === "agency" && guest.length === 0) {
    note = "Nothing corroborates this yet — no client has invited you into their workspace. "
         + "That is expected if your team holds the client access rather than you.";
  }

  return { owned, guest, employed, declared, knownAs, observed, note };
}

/** How the observed shape reads in a sentence. */
export const OBSERVED_LABEL: Record<AccountShape["observed"], string> = {
  new: "No workspaces yet",
  solo: "Works only in their own workspace",
  employee: "On the team of a business they do not own",
  collaborator: "Works for another business from outside",
  "multi-client": "Works across several clients from outside",
};

/**
 * Rename the workspace.
 *
 * Through an RPC rather than the UPDATE the RLS policy also allows: the rename
 * is audited with both names, and the old one exists nowhere else afterwards.
 * The caller refreshes the workspace context, since the name is in the switcher
 * and in half the sentences on screen.
 */
export async function renameWorkspace(workspaceId: string, name: string): Promise<string> {
  const { data, error } = await supabase.rpc("rename_workspace", {
    p_workspace_id: workspaceId,
    p_name: name,
  });
  if (error) throw asAppError(error);
  return (data as { name: string }).name;
}

/** For messages: "Ask an owner or admin to do this." */
export const ROLE_LABEL: Record<string, string> = {
  owner: "Owner",
  admin: "Admin",
  manager: "Manager",
  publisher: "Publisher",
  producer: "Producer",
  creator: "Creator",
  reviewer: "Reviewer",
  analyst: "Analyst",
  viewer: "Viewer",
};
